import React from 'react'
import { Route, Navigate } from 'react-router-dom'
import SafeComponent from '../components/SafeComponent'
import { ONPREM_DEFAULT_ROUTE } from '../config/edition'

/**
 * Marketing / landing route registration (ADR-018 Decision 4, Worker W3).
 *
 * These are the public "홍보 축" (promotion) surfaces — the landing page,
 * the /platform · /pricing · /docs · /contact marketing sub-pages, and the
 * /app SimpleHome shell. The on-prem edition does not ship them, so they are
 * gated behind ``VITE_EDITION``.
 *
 * The lazy ``import()`` calls below are guarded by the ``import.meta.env``
 * literal so Vite/Rollup can statically drop the marketing chunks from the
 * on-prem bundle (constant folding ``'onprem' === 'onprem'`` → ``true`` →
 * the ternary collapses to ``null`` and the dynamic import is removed).
 *
 * SaaS build (flag unset/other) = every route below present, unchanged.
 * on-prem build = only ``/`` and ``/app`` remain, redirecting to the app so
 * the root is never a 404 / dead-end.
 */
const marketingExcluded = import.meta.env.VITE_EDITION === 'onprem'

const LandingPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Landing/LandingPage'))
const SimpleHome = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/SimpleHome'))
const ProductsPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Marketing/ProductsPage'))
const ProductDetailPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Marketing/ProductDetailPage'))
const PlatformModuleDemoPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Marketing/PlatformModuleDemoPage'))
const SolutionsPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Marketing/SolutionsPage'))
const SolutionDetailPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Marketing/SolutionDetailPage'))
const PricingPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Marketing/PricingPage'))
const DocsPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Marketing/DocsPage'))
const DocsDetailPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Marketing/DocsDetailPage'))
const ContactPage = marketingExcluded
  ? null
  : React.lazy(() => import('../pages/Marketing/ContactPage'))

/** Paths only present in a marketing-included (SaaS) build. */
export const MARKETING_ONLY_ROUTE_PATHS = [
  '/platform',
  '/platform/:moduleId',
  '/platform/:moduleId/demo',
  '/ready-made-packs',
  '/ready-made-packs/:packId',
  '/pricing',
  '/docs',
  '/docs/:docId',
  '/contact',
] as const

/** Root paths present in every edition (marketing page vs onprem redirect). */
export const MARKETING_ROOT_ROUTE_PATHS = ['/', '/app'] as const

const lazy = (
  Component: React.LazyExoticComponent<React.ComponentType> | null,
  loadingText: string
): React.ReactElement => (
  <SafeComponent loadingText={loadingText}>
    {Component ? <Component /> : null}
  </SafeComponent>
)

/**
 * Build the marketing/landing route elements for the current edition.
 *
 * @param edition - override for tests; defaults to the build-time flag.
 * @returns Route elements to spread inside the app's <Routes>.
 */
export function buildMarketingRoutes(
  edition: string | undefined = import.meta.env.VITE_EDITION as
    | string
    | undefined
): React.ReactElement[] {
  const onprem = edition === 'onprem'

  if (onprem) {
    // On-prem has no public landing / home shell — send the root and the
    // /app entry to the app so there is no dangling 404 or dead-end.
    return [
      <Route
        key="/"
        path="/"
        element={<Navigate to={ONPREM_DEFAULT_ROUTE} replace />}
      />,
      <Route
        key="/app"
        path="/app"
        element={<Navigate to={ONPREM_DEFAULT_ROUTE} replace />}
      />,
    ]
  }

  // SaaS / default — full marketing + landing surface, unchanged.
  return [
    <Route
      key="/"
      path="/"
      element={lazy(LandingPage, '랜딩 페이지 로딩 중...')}
    />,
    <Route
      key="/app"
      path="/app"
      element={lazy(SimpleHome, '홈페이지 로딩 중...')}
    />,
    <Route
      key="/platform"
      path="/platform"
      element={lazy(ProductsPage, 'Platform 로딩 중...')}
    />,
    <Route
      key="/platform/:moduleId"
      path="/platform/:moduleId"
      element={lazy(ProductDetailPage, 'Platform module 로딩 중...')}
    />,
    <Route
      key="/platform/:moduleId/demo"
      path="/platform/:moduleId/demo"
      element={lazy(PlatformModuleDemoPage, 'Platform demo 로딩 중...')}
    />,
    <Route
      key="/ready-made-packs"
      path="/ready-made-packs"
      element={lazy(SolutionsPage, 'Ready-Made Packs 로딩 중...')}
    />,
    <Route
      key="/ready-made-packs/:packId"
      path="/ready-made-packs/:packId"
      element={lazy(SolutionDetailPage, 'Ready-made pack 로딩 중...')}
    />,
    <Route
      key="/pricing"
      path="/pricing"
      element={lazy(PricingPage, 'Pricing 로딩 중...')}
    />,
    <Route
      key="/docs"
      path="/docs"
      element={lazy(DocsPage, 'Docs 로딩 중...')}
    />,
    <Route
      key="/docs/:docId"
      path="/docs/:docId"
      element={lazy(DocsDetailPage, 'Doc 로딩 중...')}
    />,
    <Route
      key="/contact"
      path="/contact"
      element={lazy(ContactPage, 'Contact 로딩 중...')}
    />,
  ]
}
